import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const OBJECTIVES_API = '/v1/objectives';

// Helper function to safely convert date strings to Date objects
const toDate = (value: any): Date => {
    if (!value) return new Date();
    return value instanceof Date ? value : new Date(value);
};

/**
 * Interface representing an Objective.
 */
export interface Objective {
    id: string;
    addedBy: string;
    dateAdded: Date;
    dateUpdated: Date;
    owner: string;
    objectiveText: string;
    objectiveTargetDate: Date;
    updatedBy: string;
    description?: string;
    measurement?: string;
    frameworks?: string[];
    relatedRisks?: string[];
    relatedControls?: (string | number)[];
}

export interface ObjectiveCreateData {
    objectiveText: string;
    objectiveTargetDate: Date;
    owner?: string;
    description?: string;
    measurement?: string;
    frameworks?: string[];
    relatedRisks?: string[];
    relatedControls?: (string | number)[];
}

export interface ObjectiveUpdateData {
    objectiveText?: string;
    objectiveTargetDate?: Date;
    owner?: string;
    description?: string;
    measurement?: string;
    frameworks?: string[];
    relatedRisks?: string[];
    relatedControls?: (string | number)[];
}

// Helper to get headers
function getHeaders(companyID: string) {
    return {
        'X-Tenancy-Id': companyID,
        'Company-ID': companyID,
    };
}

// Helper to parse objective from API response
function parseObjective(objective: any): Objective {
    return {
        id: objective.id || '',
        addedBy: objective.addedBy || '',
        dateAdded: toDate(objective.dateAdded),
        dateUpdated: toDate(objective.dateUpdated),
        owner: objective.owner || '',
        objectiveText: objective.objectiveText || '',
        objectiveTargetDate: toDate(objective.objectiveTargetDate),
        updatedBy: objective.updatedBy || '',
        description: objective.description || '',
        measurement: objective.measurement || '',
        frameworks: Array.isArray(objective.frameworks) ? objective.frameworks : [],
        relatedRisks: Array.isArray(objective.relatedRisks) ? objective.relatedRisks : [],
        relatedControls: Array.isArray(objective.relatedControls) ? objective.relatedControls : [],
    };
}

/**
 * Fetches all objectives via Thalassa API.
 * @returns A promise that resolves to an array of Objective objects.
 */
export async function getAllObjectives(companyID: string): Promise<Objective[]> {
    try {
        const response = await deiterateAPI.GET(OBJECTIVES_API, {
            mode: 'cors',
            backend: 'thalassa',
            headers: getHeaders(companyID),
        });

        if (response.statusCode === 200 && response.objectives) {
            return response.objectives.map(parseObjective);
        }

        return [];
    } catch (error) {
        console.error('Error fetching objectives:', error);
        throw new Error('Failed to load objectives');
    }
}

/**
 * Adds a new objective via Thalassa API.
 * @param companyID - The company ID
 * @param data - The objective data
 * @returns A promise that resolves when the objective is added.
 */
export async function addObjective(
    companyID: string,
    data: ObjectiveCreateData,
): Promise<void> {
    try {
        const response = await deiterateAPI.POST(OBJECTIVES_API, {
            mode: 'cors',
            backend: 'thalassa',
            headers: getHeaders(companyID),
            body: JSON.stringify({
                ...data,
                objectiveTargetDate: data.objectiveTargetDate instanceof Date 
                    ? data.objectiveTargetDate.toISOString() 
                    : data.objectiveTargetDate,
            }),
        });

        if (response.statusCode !== 200) {
            throw new Error(response.error || 'Failed to add objective');
        }
    } catch (error) {
        console.error('Error adding objective:', error);
        throw new Error('Failed to add objective');
    }
}

/**
 * Updates an existing objective via Thalassa API.
 * @param companyID - The company ID
 * @param id - The ID of the objective to update.
 * @param updates - An object containing the fields to update.
 * @returns A promise that resolves when the objective is updated.
 */
export async function updateObjective(
    companyID: string,
    id: string,
    updates: ObjectiveUpdateData
): Promise<void> {
    try {
        const body: any = { ...updates };
        if (updates.objectiveTargetDate) {
            body.objectiveTargetDate = updates.objectiveTargetDate instanceof Date
                ? updates.objectiveTargetDate.toISOString()
                : updates.objectiveTargetDate;
        }

        const response = await deiterateAPI.PUT(`${OBJECTIVES_API}/${id}`, {
            mode: 'cors',
            backend: 'thalassa',
            headers: getHeaders(companyID),
            body: JSON.stringify(body),
        });

        if (response.statusCode !== 200) {
            throw new Error(response.error || 'Failed to update objective');
        }
    } catch (error) {
        console.error('Error updating objective:', error);
        throw new Error('Failed to update objective');
    }
}

/**
 * Deletes an objective via Thalassa API.
 * @param companyID - The company ID
 * @param id - The ID of the objective to delete.
 * @returns A promise that resolves when the objective is deleted.
 */
export async function deleteObjective(companyID: string, id: string): Promise<void> {
    try {
        const response = await deiterateAPI.DELETE(`${OBJECTIVES_API}/${id}`, {
            mode: 'cors',
            backend: 'thalassa',
            headers: getHeaders(companyID),
        });

        if (response.statusCode !== 200) {
            throw new Error(response.error || 'Failed to delete objective');
        }
    } catch (error) {
        console.error('Error deleting objective:', error);
        throw new Error('Failed to delete objective');
    }
}

/**
 * Polling-based listener for objectives changes.
 * Note: Real-time listeners require Firestore SDK. This polls the API instead.
 * @param companyID - The company ID
 * @param callback - A function that will be called with the updated array of objectives.
 * @param pollInterval - Polling interval in milliseconds (default: 30000)
 * @returns A function to unsubscribe from the listener.
 */
export function onObjectivesChange(
    companyID: string,
    callback: (objectives: Objective[]) => void,
    pollInterval: number = 30000
): () => void {
    let isActive = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const poll = async () => {
        if (!isActive) return;

        try {
            const objectives = await getAllObjectives(companyID);
            if (isActive) {
                callback(objectives);
            }
        } catch (error) {
            console.error('Error polling objectives:', error);
        }

        if (isActive) {
            timeoutId = setTimeout(poll, pollInterval);
        }
    };

    // Initial fetch
    poll();

    // Return unsubscribe function
    return () => {
        isActive = false;
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
}

export default {
    getAllObjectives,
    addObjective,
    updateObjective,
    deleteObjective,
    onObjectivesChange,
};
